<script setup lang="ts" generic="DataTableRow extends Record<string, any>">
import { toast } from '@/composables/baseToast'
import createCSVBlob from '@/helpers/createCSVBlob'
import exportFile from '@/helpers/exportFile'
import groupBy from '@/helpers/groupBy'
import Papa from 'papaparse'
import { computed, nextTick, onMounted, reactive, ref, toRaw, watch } from 'vue'
import BaseAutocomplete from './BaseAutocomplete.vue'
import BaseButton from './BaseButton.vue'
import BaseModal from './BaseModal.vue'
import BasePopover from './BasePopover.vue'

export type DataTableColumn<DataTableRow> = {
  /**Names should be unique as they are used as keys for the columns */
  name: string
  /**The key to the row data record to be displayed for the column or
   * a function that gets the value from the row data.
   */
  columnDataKey: keyof DataTableRow
  /**Default column types are provided but custom types names can be used to pass to custom column types in slots */
  columnType?: 'text' | 'number' | 'select' | 'disabled' | 'boolean' | (string & {})
  /**Options passed to the default select component. Gets passed the row data when the select is focused
   * so that the options can be filtered.
   */
  selectOptions?: (row: DataTableRow) =>
    | {
        name: string
        value: string | number
      }[]
    | undefined
  /**Default column width in px. Defaults to 100px. Columns have a minimum width of 50px. */
  defaultWidth?: number
  /**Default column sort order. */
  defaultSortOrder?: number
  /**Default column hidden property. Defaults to false. */
  deafultHidden?: boolean
  textDisplayFunction?: (value: DataTableRow[keyof DataTableRow]) => string
  /**Function that runs on cell value change before emitting change and add events */
  validator?: (
    value: DataTableRow[keyof DataTableRow],
    row: DataTableRow,
  ) => Promise<boolean> | boolean
  /**requires that a value not be undefined be present before any add or update events fire. */
  required?: boolean
  /**Receives the value from the csv and returns the correct cell value for the datatable row.
   * Most often used to map select item names to their correct values.
   */
  importConversion?: (
    value: string,
  ) => Promise<DataTableRow[keyof DataTableRow]> | DataTableRow[keyof DataTableRow]
}

export type DataTableGroup<DataTableRow> = {
  /**Name of the group by option. Must be unique to the group. */
  name: string
  /**A function that receives the row and returns a string representing the group id (name).
   * For example a group named "Division" may lookup the division_id on the row and return
   * a string formatted as `${division.code} ${division.name}` and all rows that return the
   * same string will be within that group.
   */
  fn?: (row: DataTableRow) => string | undefined
}

//
// PROPS AND EMITS
//
const props = defineProps<{
  /**The unique row data property to identify the row */
  rowKey: keyof DataTableRow
  showNewRow?: boolean
  showGroupNewRow?: boolean
  showRowActions?: boolean
  /**Table name should be unique. Custom table config like column widths and order will be stored in
   * localstorage with this as the key
   */
  tableName: string
  /**Show sort toggle on column headings */
  sortableColumns?: boolean
  /**Groups available to the group selector on the table */
  groups?: DataTableGroup<DataTableRow>[]
  validationErrorHandler?: (err: Error) => unknown
  /**Show import from csv option */
  allowImport?: boolean
  selectableRows?: 'single' | 'multiple'
}>()

/**The data record available to each row */
const rows = defineModel<DataTableRow[]>('rows', { required: true })
const columns = defineModel<DataTableColumn<DataTableRow>[]>('columns', { required: true })
/**The row[props.rowKey] values currently selected */
const selectedRows = defineModel<Set<DataTableRow[keyof DataTableRow]>>('selectedRows', {
  default: new Set(),
})

const emit = defineEmits<{
  rowUpdate: [rowData: DataTableRow]
  newRowUpdate: [rowData: DataTableRow]
  addNewRow: [rowData: DataTableRow]
  rowDblClick: [rowData: DataTableRow]
  rowSelect: [rowData: DataTableRow]
  rowUnselect: [rowData: DataTableRow]
}>()
//
// END PROPS AND EMITS
//

//
// LOCAL TABLE CONFIG
//
/**Config data stored in the users browser for this table name */
type LocalTableConfig = {
  groupByName: string | undefined
  columns: Record<
    DataTableColumn<DataTableRow>['name'],
    {
      columnWidthPx: number
      columnSortOrder: number
      columnHidden: boolean
    }
  >
}

/**Key used to force a rerender on the table when sorting on load */
const tableKey = ref(1)

// Load column defaults from props on initial render
function loadColumns() {
  const tableConfig: LocalTableConfig = { groupByName: undefined, columns: {} }
  let sortOrder = 0
  columns.value.forEach((col) => {
    tableConfig.columns[col.name] = {
      columnSortOrder: col.defaultSortOrder ?? sortOrder,
      columnWidthPx: col.defaultWidth ?? 100,
      columnHidden: col.deafultHidden ?? false,
    }
    sortOrder += 1
  })
  return tableConfig
}
const localTableConfig = ref<LocalTableConfig>(loadColumns())

/**Pulls current table configuration and stores it in the browsers localStorage */
function saveLocalTableConfig() {
  const localTableConfigString = JSON.stringify(toRaw(localTableConfig.value))
  localStorage.setItem(`dt-${props.tableName}`, localTableConfigString)
}

function sortColumns() {
  columns.value.sort(
    (a, b) =>
      (localTableConfig.value.columns[a.name].columnSortOrder ?? 1) -
      (localTableConfig.value.columns[b.name].columnSortOrder ?? 1),
  )
}

const rowActionsColumnWidth = ref(0)
function updateRowActionsColumnWidth() {
  // Find content width of cells with id="rowActionsCell"
  const rowActionsCells = document.getElementsByName('rowActionsCell')
  let maxRowActionsCellWidth = 0
  rowActionsCells.forEach((cell) => {
    const contentWidth = cell.scrollWidth
    if (contentWidth > maxRowActionsCellWidth) {
      maxRowActionsCellWidth = contentWidth
    }
  })
  if (maxRowActionsCellWidth !== rowActionsColumnWidth.value) {
    rowActionsColumnWidth.value = maxRowActionsCellWidth
  }
}

onMounted(() => {
  // Load column widths and sort order from local storage if they exist and update the column.defaultWidth
  const tableConfigString = localStorage.getItem(`dt-${props.tableName}`)
  if (!tableConfigString) {
    return
  }
  const tableConfig: Partial<LocalTableConfig> = JSON.parse(tableConfigString)
  if (tableConfig.columns) {
    Object.entries(tableConfig.columns).forEach((entry) => {
      const colName = entry[0]
      const config = entry[1]
      if (localTableConfig.value.columns[colName]) {
        localTableConfig.value.columns[colName].columnWidthPx = config.columnWidthPx
        localTableConfig.value.columns[colName].columnSortOrder = config.columnSortOrder
        localTableConfig.value.columns[colName].columnHidden = config.columnHidden
      }
    })
  }
  if (tableConfig.groupByName) {
    localTableConfig.value.groupByName = tableConfig.groupByName
    const group = props.groups?.find((g) => g.name === tableConfig.groupByName)
    if (group) {
      dtGroupBy.value = group
    }
  }
  sortColumns()
  tableKey.value += 1
  setTimeout(() => {
    updateRowActionsColumnWidth()
  }, 150)
})
//
// END LOCAL TABLE CONFIG
//

//
// ROW GROUPS
//
const dtGroupBy = ref<DataTableGroup<DataTableRow>>()
if (props.groups && props.groups.length === 1) {
  dtGroupBy.value = props.groups[0] // Only one group option passed. Use it as the default
}
const groupedRows = computed<Record<string, DataTableRow[]>>(() => {
  if (dtGroupBy.value?.fn) {
    return groupBy(rows.value, dtGroupBy.value.fn, 'none')
  }
  return { none: rows.value }
})

watch(dtGroupBy, () => {
  localTableConfig.value.groupByName = dtGroupBy.value?.name
  saveLocalTableConfig()
})

const collapsedGroups = ref<Set<string>>(new Set())
function toggleGroupCollapsed(groupName: string) {
  if (collapsedGroups.value?.has(groupName)) {
    collapsedGroups.value.delete(groupName)
  } else {
    collapsedGroups.value?.add(groupName)
  }
}

function expandAllGroups() {
  collapsedGroups.value.clear()
}
function collapseAllGroups() {
  Object.keys(groupedRows.value).forEach((groupName) => {
    collapsedGroups.value.add(groupName)
  })
}
//
// END ROW GROUPS
//

//
// INPUT FOCUS
//
const celllFocusMap = ref<Record<string, Element>>({})
const focusedInput = ref<string>()

function focusInput(groupName: string, rowIndex: number, columnIndex: number) {
  const newFocusCell = `${groupName}:${rowIndex}:${columnIndex}`
  focusedInput.value = newFocusCell
  let focusCell: Element = celllFocusMap.value[newFocusCell]
  let focusCounter = 0
  function findAndFocusInput() {
    focusCounter += 1
    nextTick(() => {
      // nextTick ensures the input has rendered before looking for the child element
      if (focusCell.children.length >= 1) {
        const child = focusCell.children[0]
        if (child instanceof HTMLInputElement || child instanceof HTMLSelectElement) {
          child.focus()
        } else if (child.children.length >= 1 && focusCounter < 5) {
          // Recursively drill down into children elements to find a select or input
          // component
          focusCell = child
          findAndFocusInput()
        }
      }
    })
  }
  findAndFocusInput()
}
//
// END INPUT FOCUS
//

/**Returns the value of the onChange event from an input or select element */
function getEventValue(
  changeEvent: Event,
  valueType: 'text' | 'number' | 'boolean' = 'text',
): DataTableRow[keyof DataTableRow] {
  const target = changeEvent.target
  if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
    let value: string | number | boolean = target.value
    if (valueType === 'number') {
      value = Number(value)
    } else if (valueType === 'boolean') {
      value = Boolean(value)
    }
    return value as DataTableRow[keyof DataTableRow]
  }
  throw Error(
    JSON.stringify({ msg: 'Event is not from an Input or Select element', eventTarget: target }),
  )
}
/**Validates the cell input and returns an error if validation fails */
async function validateInput(
  value: DataTableRow[keyof DataTableRow],
  row: DataTableRow,
  column: DataTableColumn<DataTableRow>,
) {
  let isValid = true
  if (column.validator) {
    isValid = await Promise.resolve(column.validator(value, row))
  } else if (column.columnType === 'select') {
    // By default validate that the input matches one of the select options
  }
  if (!isValid) {
    // todo - add red cell border when input is invalid
    const rowIndex = rows.value.findIndex((r) => r[props.rowKey] === row[props.rowKey])
    const error = Error(
      JSON.stringify({
        msg: 'Invalid cell input',
        inputValue: value,
        rowNumber: rowIndex + 1,
        column: column.name,
      }),
    )
    if (props.validationErrorHandler) {
      props.validationErrorHandler(error)
      return false
    }
    throw error
  }
  return isValid
}

/**Validates an entire row */
async function validateRow(row: DataTableRow) {
  for (const column of columns.value) {
    // Ensure the row has a value for each column or set the key to undefined
    // this ensure that every key exists in the emit object
    if (!row[column.columnDataKey]) {
      row[column.columnDataKey] = undefined as DataTableRow[keyof DataTableRow]
    }
    const value = row[column.columnDataKey]
    if (column.required) {
      if (value === undefined) {
        throw Error(
          JSON.stringify({
            msg: 'Input is required',
            inputValue: value,
            column: column.name,
          }),
        )
      }
    }
    if (column.validator) {
      const isValid = await column.validator(value, row)
      if (!isValid) {
        const error = Error(
          JSON.stringify({
            msg: 'Invalid input',
            inputValue: value,
            column: column.name,
          }),
        )
        if (props.validationErrorHandler) {
          props.validationErrorHandler(error)
          return false
        }
        throw error
      }
    }
  }
  return true
}

const newRow = reactive<DataTableRow>({} as DataTableRow)

async function onRowUpdate(row: DataTableRow) {
  const rowClone = structuredClone(toRaw(row))
  if (await validateRow(rowClone)) {
    emit('rowUpdate', toRaw(row))
  }
}
/**Emits onAddNewRow and clears the new row */
async function onAddNewRow(row: DataTableRow) {
  const rowClone = structuredClone(toRaw(row))
  if (await validateRow(rowClone)) {
    emit('addNewRow', rowClone)
    Object.keys(rowClone).forEach((key) => {
      newRow[key] = undefined
    })
  }
}
/**Calls `onAddNewRow` using current newRow data. Provides a simpler way of adding a row
 * when calling it from a slot component using this as a bound function.
 */
function addNewRow() {
  onAddNewRow(newRow as DataTableRow)
}

//
// SORT & HIDE COLUMNS IN MODAL
//
/**Key used to manually force the modal data to update on change */
const columnModalUpdateKey = ref(1)
const showColumnModal = ref(false)
const columnModalSelectedColumn = ref<DataTableColumn<DataTableRow>>()
function reorderColumns(direction: 'up' | 'down') {
  const columnIndex = columns.value.findIndex(
    (c) => c.name === columnModalSelectedColumn.value?.name,
  )
  if (columnIndex === undefined) throw new Error('Unable to get column index. Reload and try again')
  const column = columns.value[columnIndex]
  const currentColPosition = localTableConfig.value.columns[column.name].columnSortOrder
  if (direction === 'up') {
    if (!currentColPosition) return
    const swapColumn = columns.value[columnIndex - 1]
    const currentSortOrder = localTableConfig.value.columns[column.name].columnSortOrder
    const currentSwapSortOrder = localTableConfig.value.columns[swapColumn.name].columnSortOrder
    localTableConfig.value.columns[column.name].columnSortOrder = currentSortOrder
      ? currentSortOrder - 1
      : 0
    localTableConfig.value.columns[swapColumn.name].columnSortOrder = currentSwapSortOrder
      ? currentSwapSortOrder + 1
      : 1
  } else if (direction === 'down') {
    if (currentColPosition && currentColPosition + 1 >= columns.value.length) return
    const swapColumn = columns.value[columnIndex + 1]
    const currentSortOrder = localTableConfig.value.columns[column.name].columnSortOrder
    const currentSwapSortOrder = localTableConfig.value.columns[swapColumn.name].columnSortOrder
    localTableConfig.value.columns[column.name].columnSortOrder = currentSortOrder
      ? currentSortOrder + 1
      : 1
    localTableConfig.value.columns[swapColumn.name].columnSortOrder = currentSwapSortOrder
      ? currentSwapSortOrder - 1
      : 0
  }
  sortColumns()
  saveLocalTableConfig()
  columnModalUpdateKey.value += 1
}

function showHideColumns(showOrHide: 'show' | 'hide') {
  if (!columnModalSelectedColumn.value) return
  if (showOrHide === 'show') {
    localTableConfig.value.columns[columnModalSelectedColumn.value.name].columnHidden = false
  } else if (showOrHide === 'hide') {
    localTableConfig.value.columns[columnModalSelectedColumn.value.name].columnHidden = true
  }
  columnModalSelectedColumn.value = undefined
  saveLocalTableConfig()
}
//
// SORT & HIDE COLUMNS IN MODAL
//

//
// DRAG TO RESIZE
//
let mouseStartX = 0
let startWidth = 0
let currentColName: DataTableColumn<DataTableRow>['name'] | null = null
let resizeTimeout: number | null = null
function onColumnResize(e: MouseEvent) {
  if (currentColName === null) return
  if (resizeTimeout) return
  const dx = e.clientX - mouseStartX
  const newWidth = Math.max(startWidth + dx, 50) // minimum width
  localTableConfig.value.columns[currentColName].columnWidthPx = newWidth
  // columns.value[currentColIndex!].defaultWidth = newWidth

  resizeTimeout = setTimeout(() => {
    // Debounce this to 20ms to prevent updating on every event fire
    resizeTimeout = null
  }, 20) as unknown as number
}
function stopColumnResize() {
  currentColName = null
  mouseStartX = 0
  startWidth = 0
  document.removeEventListener('mousemove', onColumnResize)
  document.removeEventListener('mouseup', stopColumnResize)
  saveLocalTableConfig()
}
function startColumnResize(e: MouseEvent, columnName: string) {
  startWidth = localTableConfig.value.columns[columnName].columnWidthPx ?? 100
  mouseStartX = e.clientX
  currentColName = columnName

  document.addEventListener('mousemove', onColumnResize)
  document.addEventListener('mouseup', stopColumnResize)
}
//
// END DRAG TO RESIZE
//

//
// UTILITY FUNCTIONS
//
function exportToCsv() {
  let csvBlob: Blob
  if (rows.value.length === 0) {
    const exampleRow: Record<string, string> = {}
    columns.value.forEach((c) => {
      exampleRow[c.name] = ''
    })
    csvBlob = createCSVBlob([exampleRow])
  } else {
    csvBlob = createCSVBlob(toRaw(rows.value))
  }
  exportFile(csvBlob, `${props.tableName}.csv`)
}

function resetColumnWidths() {
  columns.value.forEach((col) => {
    localTableConfig.value.columns[col.name].columnWidthPx = col.defaultWidth ?? 100
  })
  saveLocalTableConfig()
}

const rowSortByColumnSelection = ref<{
  name: keyof DataTableRow
  direction: 'ascending' | 'descending'
}>()
function sortRowsDefault(columnDataKey: keyof DataTableRow) {
  if (!rowSortByColumnSelection.value) {
    rowSortByColumnSelection.value = { name: columnDataKey, direction: 'descending' }
  }
  const column = columns.value.find((c) => c.columnDataKey === columnDataKey)
  if (!column) throw Error('Unable to find column during sort operation')
  if (rowSortByColumnSelection.value.direction === 'ascending') {
    rows.value = [...rows.value].sort((a, b) => {
      if (column.columnType === 'select' && column.textDisplayFunction) {
        const displayTextA = column.textDisplayFunction(a[columnDataKey])
        const displayTextB = column.textDisplayFunction(b[columnDataKey])
        return displayTextA.localeCompare(displayTextB)
      } else {
        return String(a[columnDataKey]).localeCompare(String(b[columnDataKey]))
      }
    })
    rowSortByColumnSelection.value.direction = 'descending'
  } else {
    rows.value = [...rows.value].sort((a, b) => {
      if (column.columnType === 'select' && column.textDisplayFunction) {
        const displayTextA = column.textDisplayFunction(a[columnDataKey])
        const displayTextB = column.textDisplayFunction(b[columnDataKey])
        return displayTextB.localeCompare(displayTextA)
      } else {
        return String(b[columnDataKey]).localeCompare(String(a[columnDataKey]))
      }
    })
    rowSortByColumnSelection.value.direction = 'ascending'
  }
}

const dtCSVImport = ref<HTMLInputElement>()
const csvImportModal = ref<HTMLDialogElement>()
const csvImportData = ref<{
  /**Maps the datatable column name to the imported field name */
  tableColumnsMap: Record<string, string | undefined>
  /**Names of the imported fields */
  importedFileColumns: string[]
  /**Rows of the imported data */
  rows: Array<Record<string, string | number | boolean>>
  importMethod: 'Bypass Existing' | 'Insert All Records'
  insertUniqueColumnKey?: string
}>({ tableColumnsMap: {}, importedFileColumns: [], rows: [], importMethod: 'Insert All Records' })
async function loadFromCSV(event: Event) {
  const target = event.target as HTMLInputElement | undefined
  if (!target || !target.files) throw Error('Unable to get file')
  const csvFile = target.files[0]
  const result: Papa.ParseResult<Record<string, string | number | boolean>> = await new Promise(
    (resolve, reject) => {
      Papa.parse<Record<string, string | number | boolean>>(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.errors.length > 0) {
            reject(result.errors[0])
          }
          resolve(result)
        },
      })
    },
  )
  columns.value.forEach((column) => {
    csvImportData.value.tableColumnsMap[column.name] = undefined
  })
  csvImportData.value.importedFileColumns = result.meta.fields ?? []
  csvImportModal.value?.showModal()
  csvImportData.value.rows = result.data
}

async function importCSV() {
  columns.value.forEach((column) => {
    // Verify that all required columns are mapped
    if (column.required) {
      if (csvImportData.value.tableColumnsMap[column.name] === undefined) {
        throw Error(`Column "${column.name}" must have a mapped value`)
      }
    }
  })
  csvImportModal.value?.close()
  const datatableRows: DataTableRow[] = []
  for (const row of csvImportData.value.rows) {
    // Validate all rows
    const datatableRow: DataTableRow = {} as DataTableRow
    for (const columnName of Object.keys(csvImportData.value.tableColumnsMap)) {
      const columnSettings = columns.value.find((c) => c.name === columnName)
      const importedFieldName = csvImportData.value.tableColumnsMap[columnName]
      let value = importedFieldName ? row[importedFieldName] : undefined
      if (!columnSettings) throw Error('Unable to get column')
      try {
        if (columnSettings.importConversion && value) {
          value = await Promise.resolve(columnSettings.importConversion!(value as string))
        }
        datatableRow[columnSettings.columnDataKey] = value as DataTableRow[keyof DataTableRow]
      } catch (err) {
        // Return validation error to UI. Most likely is a problem with the import data
        toast(JSON.stringify(err), { type: 'error' })
      }
    }
    await validateRow(datatableRow as DataTableRow)
    datatableRows.push(datatableRow as DataTableRow)
  }
  for (const row of datatableRows) {
    let existingRowFound = false
    if (csvImportData.value.insertUniqueColumnKey) {
      const rowKeyValue = row[csvImportData.value.insertUniqueColumnKey]
      existingRowFound =
        rows.value.find((r) => r[csvImportData.value.insertUniqueColumnKey!] === rowKeyValue) !==
        undefined
    }
    // TODO - support 'Update Existing' import method. Possibly define unique or primary key columns in column definitions
    if (csvImportData.value.importMethod === 'Insert All Records') {
      emit('addNewRow', row)
    } else if (csvImportData.value.importMethod === 'Bypass Existing') {
      if (existingRowFound) {
        // Row exists already. Skipping...
        continue
      }
      emit('addNewRow', row)
    } else {
      throw Error('Import method must be defined')
    }
  }
  csvImportData.value = {
    tableColumnsMap: {},
    importedFileColumns: [],
    rows: [],
    importMethod: 'Bypass Existing',
    insertUniqueColumnKey: undefined,
  }
}
//
// END UTILITY FUNCTIONS
//

function toggleRowSelected(row: DataTableRow) {
  if (props.selectableRows === 'single') {
    // Check that another row isnt already selected
    if (selectedRows.value.size > 0) {
      const selectedRowsCopy = structuredClone(toRaw(selectedRows.value))
      selectedRowsCopy.forEach((rowId) => {
        selectedRows.value.delete(rowId)
        const row = rows.value.find((r) => r[props.rowKey] === rowId)
        if (!row) throw Error('Error finding row')
        emit('rowUnselect', row)
      })
      selectedRows.value.clear()
    }
  }
  if (selectedRows.value.has(row[props.rowKey])) {
    selectedRows.value.delete(row[props.rowKey])
    emit('rowUnselect', row)
  } else {
    selectedRows.value.add(row[props.rowKey])
    emit('rowSelect', row)
  }
}

function toggleSelectAllRows() {
  if (selectedRows.value.size === rows.value.length) {
    // Deselect all rows
    rows.value.forEach((row) => {
      selectedRows.value.delete(row[props.rowKey])
      emit('rowSelect', row)
    })
  } else {
    // Select all rows not already selected
    rows.value.forEach((row) => {
      if (!selectedRows.value.has(row[props.rowKey])) {
        selectedRows.value.add(row[props.rowKey])
      }
    })
  }
}
</script>

<template>
  <div class="flex h-fit max-h-full w-full flex-col">
    <div class="flex w-full justify-between gap-3 p-1">
      <!-- Table Actions -->
      <div class="flex items-end">
        <button
          class="cursor-pointer rounded p-1 hover:bg-gray-200"
          @click="resetColumnWidths()"
          title="Restore column widths to default"
        >
          <svg
            class="h-5 w-5 text-gray-800"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m16 4 3 3H5v3m3 10-3-3h14v-3m-9-2.5 2-1.5v4"
            />
          </svg>
        </button>
        <button
          class="cursor-pointer rounded p-1 hover:bg-gray-200"
          @click="exportToCsv()"
          title="Export table data to csv"
        >
          <svg
            class="h-5 w-5 text-gray-800"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 10V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1v6M5 19v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-1M10 3v4a1 1 0 0 1-1 1H5m2.665 9H6.647A1.647 1.647 0 0 1 5 15.353v-1.706A1.647 1.647 0 0 1 6.647 12h1.018M16 12l1.443 4.773L19 12m-6.057-.152-.943-.02a1.34 1.34 0 0 0-1.359 1.22 1.32 1.32 0 0 0 1.172 1.421l.536.059a1.273 1.273 0 0 1 1.226 1.718c-.2.571-.636.754-1.337.754h-1.13"
            />
          </svg>
        </button>
        <div v-if="props.allowImport" class="flex items-start">
          <button
            class="cursor-pointer rounded p-1 hover:bg-gray-200"
            @click="
              () => {
                dtCSVImport?.click()
              }
            "
            title="Load Records from File"
          >
            <input
              type="file"
              id="dtCSVImport"
              accept=".csv"
              style="display: none"
              @change="(e) => loadFromCSV(e)"
              ref="dtCSVImport"
            />
            <svg
              class="size-5 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 5v9m-5 0H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-2M8 9l4-5 4 5m1 8h.01"
              />
            </svg>
          </button>
          <dialog
            ref="csvImportModal"
            class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform rounded-lg border border-gray-300 p-6 backdrop-blur-xl"
          >
            <div class="flex flex-col gap-3">
              <div class="flex w-full justify-between">
                <h4 class="text-lg font-bold text-nowrap">CSV Import Settings</h4>
              </div>
              <div>
                <label for="importMethod" class="mb-2 block text-sm font-medium text-gray-900"
                  >Import Method</label
                >
                <select
                  v-model="csvImportData.importMethod"
                  class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="Insert All Records">Insert All Records</option>
                  <option value="Bypass Existing">Bypass Existing</option>
                </select>
              </div>
              <div v-if="csvImportData.importMethod !== 'Insert All Records'">
                <label for="importRowKey" class="mb-2 block text-sm font-medium text-gray-900"
                  >Unique Column</label
                >
                <select
                  v-model="csvImportData.insertUniqueColumnKey"
                  class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  title="The column to use as the unique row identifier"
                >
                  <option
                    v-for="column in columns"
                    :key="column.name"
                    :value="column.columnDataKey"
                  >
                    {{ column.name }}
                  </option>
                </select>
              </div>
              <div>
                <label for="importMap" class="mb-2 block text-sm font-medium text-gray-900"
                  >Map Columns</label
                >
                <table id="importMap">
                  <thead class="bg-gray-200 font-bold">
                    <tr>
                      <th class="border border-gray-300 px-2">Import Target</th>
                      <th class="border border-gray-300 px-2">Import Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="tableColumnName in Object.keys(csvImportData.tableColumnsMap)"
                      :key="tableColumnName"
                      class="h-8 max-h-8 odd:bg-gray-50 even:bg-white"
                    >
                      <td class="border border-gray-300 px-1">{{ tableColumnName }}</td>
                      <td class="border border-gray-300 px-1">
                        <select
                          v-model="csvImportData.tableColumnsMap[tableColumnName]"
                          class="w-full"
                        >
                          <option :value="undefined"></option>
                          <option
                            v-for="importColumn in csvImportData.importedFileColumns"
                            :key="importColumn"
                            :value="importColumn"
                          >
                            {{ importColumn }}
                          </option>
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="flex gap-1">
                <BaseButton @click="csvImportModal?.close()" status="secondary">Cancel</BaseButton>
                <BaseButton @click="importCSV()">Save</BaseButton>
              </div>
            </div>
          </dialog>
        </div>
        <div
          v-if="Object.keys(groupedRows).length > 1 || !Object.keys(groupedRows).includes('none')"
          class="flex"
        >
          <button
            @click="collapseAllGroups()"
            class="cursor-pointer rounded p-1 hover:bg-gray-200"
            title="Collapse all groups"
          >
            <!-- Collapse rows -->
            <svg
              class="h-5 w-5 rotate-90 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8.99994 10 7 11.9999l1.99994 2M12 5v14M5 4h14c.5523 0 1 .44772 1 1v14c0 .5523-.4477 1-1 1H5c-.55228 0-1-.4477-1-1V5c0-.55228.44772-1 1-1Z"
              />
            </svg>
          </button>
          <button
            @click="expandAllGroups()"
            class="cursor-pointer rounded p-1 hover:bg-gray-200"
            title="Expand all groups"
          >
            <!-- Expand rows -->
            <svg
              class="h-5 w-5 rotate-90 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m7 10 1.99994 1.9999-1.99994 2M12 5v14M5 4h14c.5523 0 1 .44772 1 1v14c0 .5523-.4477 1-1 1H5c-.55228 0-1-.4477-1-1V5c0-.55228.44772-1 1-1Z"
              />
            </svg>
          </button>
        </div>
        <BasePopover v-if="props.groups">
          <template #popoverTrigger>
            <div
              class="flex cursor-pointer items-end gap-1 p-1 text-sm text-blue-600 hover:text-blue-800"
            >
              Group by
              <svg
                class="size-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fill-rule="evenodd"
                  d="M18.425 10.271C19.499 8.967 18.57 7 16.88 7H7.12c-1.69 0-2.618 1.967-1.544 3.271l4.881 5.927a2 2 0 0 0 3.088 0l4.88-5.927Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </template>
          <template #popoverBody>
            <div class="w-full rounded border border-gray-300 bg-white p-3">
              <label for="groupBy" class="block text-xs font-medium text-gray-900">Group By</label>
              <select
                v-model="dtGroupBy"
                class="block rounded-lg border border-gray-300 bg-gray-50 p-1 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
              >
                <option v-for="group in props.groups" :key="group.name" :value="group">
                  {{ group.name }}
                </option>
              </select>
            </div>
          </template>
        </BasePopover>
      </div>
      <div class="flex">
        <slot name="tableActions" />
      </div>
    </div>
    <div class="h-full w-full overflow-auto border-gray-300 bg-gray-200">
      <table
        class="h-full w-full table-fixed border-collapse rounded-md border border-gray-300"
        :key="tableKey"
      >
        <thead class="sticky top-0 z-3 bg-gray-200 font-bold">
          <tr>
            <th class="border border-r-2 border-gray-300" style="width: 15px">
              <!-- Row Selector -->
              <button
                @click="showColumnModal = true"
                class="cursor-pointer hover:bg-gray-200"
                title="Configure columns"
              >
                <svg
                  class="h-3 w-3 text-gray-800"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 5v14M9 5v14M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"
                  />
                </svg>
              </button>
            </th>
            <th
              v-if="props.selectableRows"
              class="border border-r-2 border-gray-300"
              style="width: 25px"
            >
              <div
                class="flex h-full w-full items-center justify-center"
                @click="toggleSelectAllRows"
              >
                <div
                  :class="`flex size-4 items-center justify-center rounded border border-gray-300 ${selectedRows.size === rows.length ? 'bg-blue-500' : 'bg-white'}`"
                >
                  <svg
                    class="size-3 text-white"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 11.917 9.724 16.5 19 7.5"
                    />
                  </svg>
                </div>
              </div>
            </th>
            <th
              v-for="column in columns"
              :key="column.name"
              :style="{ width: localTableConfig.columns[column.name].columnWidthPx + 'px' }"
              class="border border-gray-300 pl-1 text-start"
              v-show="!localTableConfig.columns[column.name].columnHidden"
            >
              <div class="flex items-center justify-between">
                <div class="truncate" :title="column.name">{{ column.name }}</div>
                <div class="flex">
                  <button
                    @click="sortRowsDefault(column.columnDataKey)"
                    v-if="props.sortableColumns"
                    class="cursor-pointer"
                  >
                    <svg
                      class="size-3 text-gray-800"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M12.832 3.445a1 1 0 0 0-1.664 0l-4 6A1 1 0 0 0 8 11h8a1 1 0 0 0 .832-1.555l-4-6Zm-1.664 17.11a1 1 0 0 0 1.664 0l4-6A1 1 0 0 0 16 13H8a1 1 0 0 0-.832 1.555l4 6Z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                    class="h-8 w-2 cursor-col-resize hover:bg-black"
                    @mousedown="startColumnResize($event, column.name)"
                  >
                    <!-- Drag handle to resize columns -->
                  </button>
                </div>
              </div>
            </th>
            <th
              v-if="props.showRowActions"
              class="w-auto border border-l-2 border-gray-300"
              :style="{ width: String(rowActionsColumnWidth) + 'px', minWidth: 'max-content' }"
            ></th>
          </tr>
        </thead>
        <tbody class="overflow-auto">
          <tr v-if="props.showNewRow" class="h-8 max-h-8 odd:bg-gray-50 even:bg-white">
            <td class="border border-r-2 border-gray-300 bg-gray-100"></td>
            <td v-if="props.selectableRows" class="border border-gray-300 p-1"></td>
            <td
              :key="column.name"
              v-for="column in columns"
              v-show="!localTableConfig.columns[column.name].columnHidden"
              class="truncate border border-gray-300 p-1"
            >
              <BaseAutocomplete
                v-if="column.columnType === 'select'"
                v-model="newRow[column.columnDataKey]"
                @select="
                  async (selectedOption) => {
                    await validateInput(
                      selectedOption.value as DataTableRow[keyof DataTableRow],
                      newRow,
                      column,
                    )
                    newRow[column.columnDataKey] = selectedOption.value
                  }
                "
                :suggestions="column.selectOptions!(newRow)"
                suggestions-key="name"
                option-label="name"
              />
              <input
                v-else-if="column.columnType === 'number'"
                class="box w-full"
                type="number"
                min="0"
                :value="newRow[column.columnDataKey]"
                @change="
                  async (event) => {
                    const value = getEventValue(event, 'number')
                    await validateInput(value, newRow, column)
                    newRow[column.columnDataKey] = value
                  }
                "
                onfocus="this.select()"
              />
              <input
                v-else-if="column.columnType === 'boolean'"
                class="box w-full"
                type="checkbox"
                :value="newRow[column.columnDataKey]"
                :checked="newRow[column.columnDataKey]"
                @change="
                  async (event) => {
                    const value = getEventValue(event, 'boolean')
                    await validateInput(value, newRow, column)
                    newRow[column.columnDataKey] = value
                  }
                "
              />
              <!-- The remaining standard column type is text. If defined as another type it is custom. -->
              <slot
                v-else-if="column.columnType !== 'text'"
                name="customCellEditor"
                :column="column"
                :newRow="newRow"
                :addNewRow="addNewRow"
              >
              </slot>
              <input
                v-else
                class="box w-full"
                :value="newRow[column.columnDataKey]"
                :checked="newRow[column.columnDataKey]"
                @change="
                  async (event) => {
                    const value = getEventValue(event)
                    await validateInput(value, newRow, column)
                    newRow[column.columnDataKey] = value
                  }
                "
                :disabled="column.columnType === 'disabled'"
                onfocus="this.select()"
              />
            </td>
            <td
              v-if="props.showRowActions"
              class="border border-l-2 border-gray-300 p-1"
              name="rowActionsCell"
            >
              <slot
                name="newRowActions"
                :newRow="newRow"
                :onAddNewRow="onAddNewRow"
                :addNewRow="addNewRow"
              >
                <!-- Slot used for custom row actions like save or delete -->
                <button
                  class="cursor-pointer p-1 hover:bg-gray-200"
                  @click="addNewRow"
                  title="Save new item"
                >
                  <svg
                    class="size-4 text-gray-800"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-width="2"
                      d="M11 16h2m6.707-9.293-2.414-2.414A1 1 0 0 0 16.586 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7.414a1 1 0 0 0-.293-.707ZM16 20v-6a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v6h8ZM9 4h6v3a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V4Z"
                    />
                  </svg>
                </button>
              </slot>
            </td>
          </tr>
          <template v-for="(groupRows, groupName) in groupedRows" :key="groupName">
            <tr v-if="groupName !== 'none'" class="h-8">
              <!-- Group Header -->
              <td class="border border-r-2 border-gray-300 bg-gray-100"></td>
              <td v-if="props.selectableRows" class="border border-gray-300 bg-gray-200"></td>
              <!-- prettier-ignore -->
              <td v-if="$slots.groupHeaderCell /* eslint-disable-line vue/no-use-v-if-with-v-for */"
                class="border border-gray-300 bg-gray-200 px-1"
                :key="column.name"
                v-for="column in columns"
              >
                <slot
                  name="groupHeaderCell"
                  :column="column"
                  :groupName="groupName"
                  :groupRows="groupRows"
                  :toggleGroupCollapsed="toggleGroupCollapsed"
                >
                </slot>
              </td>
              <td v-else :colspan="columns.length" @click="toggleGroupCollapsed(groupName)">
                <div class="flex cursor-default flex-nowrap items-center">
                  <svg
                    v-show="collapsedGroups.has(groupName)"
                    class="size-4 text-gray-800"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10.271 5.575C8.967 4.501 7 5.43 7 7.12v9.762c0 1.69 1.967 2.618 3.271 1.544l5.927-4.881a2 2 0 0 0 0-3.088l-5.927-4.88Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    v-show="!collapsedGroups.has(groupName)"
                    class="size-4 text-gray-800"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18.425 10.271C19.499 8.967 18.57 7 16.88 7H7.12c-1.69 0-2.618 1.967-1.544 3.271l4.881 5.927a2 2 0 0 0 3.088 0l4.88-5.927Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="px-3">{{ groupName }}</span>
                </div>
              </td>
              <td v-if="props.showRowActions" class="border border-l-2 border-gray-300 p-1"></td>
            </tr>
            <tr
              v-for="(row, rowIndex) in groupRows"
              :key="row[props.rowKey]"
              class="group h-8 max-h-8 odd:bg-gray-50 even:bg-white"
              @dblclick="emit('rowDblClick', row)"
              v-show="!collapsedGroups.has(groupName)"
            >
              <td class="border border-r-2 border-gray-300 bg-gray-100"></td>
              <td
                v-if="props.selectableRows"
                class="border border-gray-300"
                @click="toggleRowSelected(row)"
              >
                <div class="flex h-full w-full items-center justify-center">
                  <div
                    :class="`flex size-4 items-center justify-center rounded border border-gray-300 ${selectedRows.has(row[props.rowKey]) ? 'bg-blue-500' : 'bg-white'}`"
                  >
                    <svg
                      class="size-3 text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 11.917 9.724 16.5 19 7.5"
                      />
                    </svg>
                  </div>
                </div>
              </td>
              <td
                :key="column.name"
                v-for="(column, columnIndex) in columns"
                v-show="!localTableConfig.columns[column.name].columnHidden"
                class="truncate border border-gray-300 p-1"
                @click.left="focusInput(groupName, rowIndex, columnIndex)"
                tabindex="0"
                @focus="focusInput(groupName, rowIndex, columnIndex)"
                @keyup.esc="focusedInput = undefined"
                :ref="
                  (el) => {
                    const cellId: string = `${groupName}:${rowIndex}:${columnIndex}`
                    celllFocusMap[cellId] = el as Element
                  }
                "
              >
                <!-- Show slot if the cell is focused -->
                <input
                  v-if="column.columnType === 'boolean'"
                  class="box h-full w-full border"
                  type="checkbox"
                  :value="row[column.columnDataKey]"
                  :checked="row[column.columnDataKey]"
                  @change="
                    async (event) => {
                      const value = getEventValue(event, 'boolean')
                      await validateInput(value, row, column)
                      row[column.columnDataKey] = value
                      onRowUpdate(row)
                    }
                  "
                />
                <slot
                  v-else-if="
                    focusedInput === `${groupName}:${rowIndex}:${columnIndex}` &&
                    column.columnType !== 'disabled'
                  "
                  name="cellEditor"
                  :column="column"
                  :row="row"
                  :onRowUpdate="onRowUpdate"
                >
                  <BaseAutocomplete
                    v-if="column.columnType === 'select'"
                    :default-query="
                      column.textDisplayFunction
                        ? column.textDisplayFunction(row[column.columnDataKey])
                        : ''
                    "
                    @select="
                      async (selectedOption) => {
                        await validateInput(
                          selectedOption.value as DataTableRow[keyof DataTableRow],
                          row,
                          column,
                        )
                        row[column.columnDataKey] =
                          selectedOption.value as DataTableRow[keyof DataTableRow]
                        onRowUpdate(row)
                      }
                    "
                    :suggestions="column.selectOptions!(row)"
                    suggestions-key="name"
                    option-label="name"
                  />
                  <input
                    v-else-if="column.columnType === 'number'"
                    class="box h-full w-full border"
                    type="number"
                    min="0"
                    :value="row[column.columnDataKey]"
                    @change="
                      async (event) => {
                        const value = getEventValue(event, 'number')
                        await validateInput(value, row, column)
                        row[column.columnDataKey] = value
                        onRowUpdate(row)
                      }
                    "
                    onfocus="this.select()"
                  />
                  <input
                    v-else
                    class="box h-full w-full border"
                    :value="row[column.columnDataKey]"
                    @change="
                      async (event) => {
                        const value = getEventValue(event)
                        await validateInput(value, row, column)
                        row[column.columnDataKey] = value
                        onRowUpdate(row)
                      }
                    "
                    onfocus="this.select()"
                  />
                </slot>
                <slot v-else name="cellTextDisplay" :column="column" :row="row">
                  <!-- Normally use the column.textDisplayFunction to convert this value for display -->
                  <input
                    v-if="column.columnType === 'boolean'"
                    class="box h-full w-full border"
                    type="checkbox"
                    :value="row[column.columnDataKey]"
                    :checked="row[column.columnDataKey]"
                    @change="
                      async (event) => {
                        const value = getEventValue(event, 'boolean')
                        await validateInput(value, row, column)
                        row[column.columnDataKey] = value
                        onRowUpdate(row)
                      }
                    "
                  />
                  <span v-else>
                    {{
                      column.textDisplayFunction
                        ? column.textDisplayFunction(row[column.columnDataKey])
                        : row[column.columnDataKey]
                    }}
                  </span>
                </slot>
              </td>
              <td
                v-if="props.showRowActions"
                class="border border-l-2 border-gray-300 p-1"
                name="rowActionsCell"
              >
                <slot name="rowActions" :row="row" :rowIndex="rowIndex" :onRowUpdate="onRowUpdate">
                  <!-- Slot used for custom row actions like save or delete -->
                </slot>
              </td>
            </tr>
            <tr
              v-show="!collapsedGroups.has(groupName)"
              v-if="props.showGroupNewRow"
              class="h-8 max-h-8 odd:bg-gray-50 even:bg-white"
            >
              <td class="border border-r-2 border-gray-300 bg-gray-100"></td>
              <td v-if="props.selectableRows" class="border border-gray-300 p-1"></td>
              <td
                :key="column.name"
                v-for="column in columns"
                v-show="!localTableConfig.columns[column.name].columnHidden"
                class="truncate border border-gray-300 p-1"
              >
                <slot
                  name="newGroupRowCellEditor"
                  :column="column"
                  :newRow="newRow"
                  :onAddNewRow="onAddNewRow"
                  :addNewRow="addNewRow"
                  :groupName="groupName"
                  :groupRows="groupRows"
                >
                  <BaseAutocomplete
                    v-if="column.columnType === 'select'"
                    v-model="newRow[column.columnDataKey]"
                    @select="
                      async (selectedOption) => {
                        await validateInput(
                          selectedOption.value as DataTableRow[keyof DataTableRow],
                          newRow,
                          column,
                        )
                        newRow[column.columnDataKey] = selectedOption.value
                      }
                    "
                    :suggestions="column.selectOptions!(newRow)"
                    suggestions-key="name"
                    option-label="name"
                  />
                  <input
                    v-else-if="column.columnType === 'number'"
                    class="box w-full"
                    type="number"
                    min="0"
                    :value="newRow[column.columnDataKey]"
                    @change="
                      async (event) => {
                        const value = getEventValue(event, 'number')
                        await validateInput(value, newRow, column)
                        newRow[column.columnDataKey] = value
                      }
                    "
                  />
                  <input
                    v-else-if="column.columnType === 'boolean'"
                    class="box w-full"
                    type="checkbox"
                    :value="newRow[column.columnDataKey]"
                    :checked="newRow[column.columnDataKey]"
                    @change="
                      async (event) => {
                        const value = getEventValue(event, 'boolean')
                        await validateInput(value, newRow, column)
                        newRow[column.columnDataKey] = value
                      }
                    "
                  />
                  <input
                    v-else
                    class="box w-full"
                    :value="newRow[column.columnDataKey]"
                    @change="
                      async (event) => {
                        const value = getEventValue(event, 'text')
                        await validateInput(value, newRow, column)
                        newRow[column.columnDataKey] = value
                      }
                    "
                    :disabled="column.columnType === 'disabled'"
                  />
                </slot>
              </td>
              <td
                v-if="props.showRowActions"
                class="border border-l-2 border-gray-300 p-1"
                name="rowActionsCell"
              >
                <slot name="newRowActions" :newRow="newRow" :addNewRow="addNewRow">
                  <!-- Slot used for custom row actions like save or delete -->
                  <button
                    class="cursor-pointer p-1 hover:bg-gray-200"
                    @click="addNewRow"
                    title="Save new item"
                  >
                    <svg
                      class="size-6 text-gray-800"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-width="2"
                        d="M11 16h2m6.707-9.293-2.414-2.414A1 1 0 0 0 16.586 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7.414a1 1 0 0 0-.293-.707ZM16 20v-6a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v6h8ZM9 4h6v3a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V4Z"
                      />
                    </svg>
                  </button>
                </slot>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <BaseModal v-model:showModal="showColumnModal" title="Column Configuration">
      <!-- Column Configurator Modal -->
      <div class="flex flex-col gap-3">
        <div class="flex h-full w-full justify-between gap-3" :key="columnModalUpdateKey">
          <div class="flex w-full flex-col">
            <span>Available Columns</span>
            <div class="flex h-full flex-col border bg-white">
              <ul>
                <li
                  v-for="column in columns.filter(
                    (c) => localTableConfig.columns[c.name].columnHidden === true,
                  )"
                  :key="column.columnDataKey"
                  @click="() => (columnModalSelectedColumn = column)"
                  class="w-full cursor-pointer px-3 text-start hover:bg-blue-500 hover:text-white"
                  :class="
                    columnModalSelectedColumn?.name == column.name ? 'bg-blue-500 text-white' : ''
                  "
                >
                  {{ column.name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="flex flex-col items-center justify-center gap-3">
            <!-- To/From Toggle Buttons -->
            <button
              class="rounded p-3"
              :class="
                columnModalSelectedColumn &&
                localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
                  ? 'cursor-pointer bg-gray-200'
                  : 'bg-gray-100'
              "
              :disabled="
                columnModalSelectedColumn &&
                !localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
              "
              @click="showHideColumns('show')"
            >
              <svg
                class="h-6 w-6 text-gray-800"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m9 5 7 7-7 7"
                />
              </svg>
            </button>
            <button
              class="rounded p-3"
              :class="
                !columnModalSelectedColumn ||
                localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
                  ? 'bg-gray-100'
                  : 'cursor-pointer bg-gray-200'
              "
              :disabled="
                !columnModalSelectedColumn ||
                localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
              "
              @click="showHideColumns('hide')"
            >
              <svg
                class="h-6 w-6 text-gray-800"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m15 19-7-7 7-7"
                />
              </svg>
            </button>
          </div>
          <div class="flex w-full flex-col">
            <span>Selected Columns</span>
            <div class="flex h-full flex-col border bg-white">
              <ul>
                <li
                  v-for="column in columns.filter(
                    (c) => localTableConfig.columns[c.name].columnHidden !== true,
                  )"
                  :key="column.name"
                  @click="() => (columnModalSelectedColumn = column)"
                  class="w-full cursor-pointer px-3 text-start hover:bg-blue-500 hover:text-white"
                  :class="
                    columnModalSelectedColumn?.name == column.name ? 'bg-blue-500 text-white' : ''
                  "
                >
                  {{ column.name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="flex flex-col items-center justify-center gap-3">
            <!-- Order Toggle Buttons -->
            <button
              class="rounded p-3"
              :class="
                !columnModalSelectedColumn ||
                localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
                  ? 'bg-gray-100'
                  : 'cursor-pointer bg-gray-200'
              "
              :disabled="
                !columnModalSelectedColumn ||
                localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
              "
              @click="reorderColumns('up')"
            >
              <svg
                class="h-6 w-6 text-gray-800"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m5 15 7-7 7 7"
                />
              </svg>
            </button>
            <button
              class="rounded p-3"
              :class="
                !columnModalSelectedColumn ||
                localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
                  ? 'bg-gray-100'
                  : 'cursor-pointer bg-gray-200'
              "
              :disabled="
                !columnModalSelectedColumn ||
                localTableConfig.columns[columnModalSelectedColumn.name].columnHidden
              "
              @click="reorderColumns('down')"
            >
              <svg
                class="h-6 w-6 text-gray-800"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m19 9-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>
        <button
          class="me-2 mb-2 w-full cursor-pointer rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none"
          @click="showColumnModal = false"
        >
          Close
        </button>
      </div>
    </BaseModal>
  </div>
</template>
